// Open PDF of ONE sales document (credit note, goodwill credit, refund voucher, payment voucher, return slip): the server A4 PDF
// (GET /sales-documents/:kind/:id/pdf, same pipeline as the invoice PDF) opened in the document modal through openDocModal.
// Shared by: the sheet / quick view footers (SalesDocPdfActions), the register / customer row actions (DocRowActions) and the 'document created'
// success states (ProposePrint). Nothing is auto-opened. Invoices and proformas keep their own sheet (InvoiceSheet / InvoiceQuickView).
import React from 'react';
import { useTranslation } from 'react-i18next';
import { IcoLn } from '../../../shell/ActionIcons';
import { Emo } from '../../../shell/EmojiIcons';
import useActionLabel from '../../../i18n/useActionLabel';
import api from '../../../services/api';
import DocPdfActions, { DocPdfIconButton, useOpenPdf } from '../../documents/DocPdfActions';

export const PRINT_KINDS = ['avoir_correction', 'avoir_commercial', 'refund_voucher', 'payment_voucher', 'return_slip'];
const useLang = () => { const { i18n } = useTranslation(); return i18n.language && i18n.language.startsWith('fr') ? 'fr' : 'en'; };

const sourceOf = (kind, id, lang) => ({ fetchPdf: () => api.getSalesDocumentPdf(kind, id, lang), printLog: id ? { kind, id } : undefined, sheet: id ? { kind, id } : undefined });

// the footer cluster of a sales document: issued -> Open PDF; draft -> Preview (+ Issue & print when onIssue is given)
export function SalesDocPdfActions({ kind, id, issued = true, onIssue, issueDisabled, issueTitle, primary = true }) {
  const lang = useLang();
  return <DocPdfActions issued={issued} onIssue={onIssue} issueDisabled={issueDisabled} issueTitle={issueTitle} primary={primary} {...sourceOf(kind, id, lang)} />;
}

// row actions (register, order credit notes, customer goodwill / compensation): one Open PDF icon. Greyed (not hidden) with the reason when `disabledReason` is set.
export function DocRowActions({ kind, id, disabledReason = '' }) {
  const lang = useLang();
  return <DocPdfIconButton disabledReason={disabledReason} {...sourceOf(kind, id, lang)} />;
}

// 'document created' success line: the caller's message + Open / Open PDF. doc = { kind, id, no }; onOpen(doc) opens the read-only view (optional). No auto-open.
export function ProposePrint({ doc, children, onOpen, testid = 'doc-propose' }) {
  const { t } = useTranslation('avoir'); const al = useActionLabel(); const lang = useLang();
  const { open, busy, err } = useOpenPdf(sourceOf(doc?.kind, doc?.id, lang));
  const printable = !!doc?.id && PRINT_KINDS.includes(doc.kind);   // no id yet (an old response): the message alone, nothing to open
  return (
    <div className="hint" role="status" data-testid={testid} style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
      <Emo name="check" /><span style={{ flex: '1 1 12rem' }}>{children}</span>
      {printable && onOpen && <button type="button" className="btn sm" onClick={() => onOpen(doc)} data-testid={`doc-open-${doc.kind}`}><IcoLn name="view" /> {t('sheet.openDoc')}</button>}
      {printable && <button type="button" className="btn sm" disabled={busy} onClick={() => open()} data-testid="doc-propose-open-pdf"><IcoLn name="invoice" /> {al('openPdf')}</button>}
      {err && <span className="ferr" role="alert">{err}</span>}
    </div>
  );
}
